import React, { createContext, useContext, useState, useCallback } from 'react';

const TourContext = createContext(null);

export const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to SafeHer',
    subtitle: 'Step 1 of 4 — Receiving Reports',
    body: 'This is the live safety map. Women in your community report incidents here in real time. Each pin shows a verified or community-reported safety concern. Tap the highlighted report near Union Square to see what a report looks like.',
    hint: 'Tap the pulsing marker on the map',
    action: 'open_spotlight', // signal to MapScreen to open tour-001
  },
  {
    id: 'report_detail',
    title: 'Reports You Can Trust',
    subtitle: 'Step 1 of 4 — Receiving Reports',
    body: 'Verified reports (marked with a shield badge) have been reviewed by a trained moderator. You can upvote a report if you\'ve witnessed the same issue — this helps the community and authorities understand how serious it is. Comments let neighbours add context.',
    hint: 'Close the report when you\'re ready',
    action: null,
  },
  {
    id: 'file_report',
    title: 'Report an Incident',
    subtitle: 'Step 2 of 4 — Reporting',
    body: 'Spotted something unsafe? Tap the + button to drop a pin, describe what happened, and choose a severity level. Your identity stays completely hidden — we never store your name. The report appears on the map immediately.',
    hint: 'Tap the + button to try filing a report',
    action: 'open_create_report', // signal to open the report form
  },
  {
    id: 'moderator_role',
    title: 'Apply to Be a Moderator',
    subtitle: 'Step 3 of 4 — Moderator Role',
    body: 'Moderators are trained community volunteers who review reports, verify incidents, and work with local authorities. They cover a specific geographic area — like a neighbourhood captain for safety. You can apply from the menu inside the app.',
    hint: null,
    action: null,
  },
  {
    id: 'mod_comms',
    title: 'Moderators Improve Every Report',
    subtitle: 'Step 4 of 4 — Moderator Communications',
    body: 'When a report needs more detail, moderators send a private message to the reporter asking follow-up questions — what time, what did the person look like, were others around? Better reports mean faster, more accurate action from authorities.',
    hint: null,
    action: 'show_inbox_demo',
  },
  {
    id: 'done',
    title: 'You\'re Ready',
    subtitle: 'Tour complete',
    body: 'SafeHer is free, anonymous, and community-powered. Create your account to start reporting, upvoting, and keeping your neighbourhood safer.',
    hint: null,
    action: 'end',
  },
];

export function TourProvider({ children }) {
  const [isTourMode, setIsTourMode]     = useState(false);
  const [stepIndex,  setStepIndex]      = useState(0);
  const [tourSignal, setTourSignal]     = useState(null); // signals to MapScreen

  const startTour = useCallback(() => {
    setStepIndex(0);
    setIsTourMode(true);
    setTourSignal(TOUR_STEPS[0].action);
  }, []);

  const nextStep = useCallback(() => {
    setStepIndex(prev => {
      const next = prev + 1;
      if (next < TOUR_STEPS.length) {
        setTourSignal(TOUR_STEPS[next].action);
      }
      return next;
    });
  }, []);

  const prevStep = useCallback(() => {
    setStepIndex(prev => {
      const back = Math.max(0, prev - 1);
      setTourSignal(null);
      return back;
    });
  }, []);

  const endTour = useCallback(() => {
    setIsTourMode(false);
    setStepIndex(0);
    setTourSignal(null);
  }, []);

  const clearSignal = useCallback(() => setTourSignal(null), []);

  return (
    <TourContext.Provider value={{
      isTourMode,
      stepIndex,
      currentStep: TOUR_STEPS[stepIndex] || null,
      tourSignal,
      startTour,
      nextStep,
      prevStep,
      endTour,
      clearSignal,
      totalSteps: TOUR_STEPS.length,
    }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside TourProvider');
  return ctx;
}
